import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Spinner } from "../Bits";
import ImageUpload from "../ImageUpload";
import ArticlesCalendar from "./ArticlesCalendar";
import { Plus, Pencil, Trash2, Sparkles, Image as ImageIcon, Eye, EyeOff, Calendar as CalendarIcon, List as ListIcon } from "lucide-react";

const CATEGORIES = [
  { value: "wissen", label: "Wissen" },
  { value: "rezept", label: "Rezepte" },
  { value: "saison", label: "Saison" },
  { value: "sicherheit", label: "Sicherheit" },
  { value: "luxemburg", label: "Luxemburg" },
];

const TONES = [
  { value: "informativ", label: "Informativ" },
  { value: "warm", label: "Warm & persönlich" },
  { value: "poetisch", label: "Poetisch" },
  { value: "locker", label: "Locker" },
];

const LENGTHS = [
  { value: "kurz", label: "Kurz (~500 W)" },
  { value: "mittel", label: "Mittel (~850 W)" },
  { value: "lang", label: "Lang (~1400 W)" },
];

// --- Tiny Markdown → HTML renderer for the preview tab (no external deps).
// Covers: # / ## / ###, **bold**, *italic*, lists, images ![alt](url), links [t](u), paragraphs.
function renderMarkdown(md) {
  if (!md) return "";
  let html = md.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // images first (before links)
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g,
    '<img alt="$1" src="$2" class="my-4 rounded-lg max-w-full" />');
  // links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" class="underline" style="color:var(--wp-olive)">$1</a>');
  // bold + italic
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");

  // line-by-line for headings / lists / paragraphs
  const lines = html.split(/\n/);
  const out = [];
  let inList = false;
  for (const raw of lines) {
    const ln = raw.trimEnd();
    if (/^###\s+/.test(ln)) { if (inList) { out.push("</ul>"); inList = false; } out.push(`<h3 class="font-display text-xl mt-6 mb-2" style="color:var(--wp-olive)">${ln.replace(/^###\s+/, "")}</h3>`); }
    else if (/^##\s+/.test(ln)) { if (inList) { out.push("</ul>"); inList = false; } out.push(`<h2 class="font-display text-2xl mt-8 mb-3" style="color:var(--wp-olive)">${ln.replace(/^##\s+/, "")}</h2>`); }
    else if (/^#\s+/.test(ln)) { if (inList) { out.push("</ul>"); inList = false; } out.push(`<h1 class="font-display text-3xl mt-8 mb-4" style="color:var(--wp-olive)">${ln.replace(/^#\s+/, "")}</h1>`); }
    else if (/^[-*]\s+/.test(ln)) { if (!inList) { out.push('<ul class="list-disc pl-6 my-2 space-y-1">'); inList = true; } out.push(`<li>${ln.replace(/^[-*]\s+/, "")}</li>`); }
    else if (ln.trim() === "") { if (inList) { out.push("</ul>"); inList = false; } out.push(""); }
    else { if (inList) { out.push("</ul>"); inList = false; } out.push(`<p class="leading-relaxed my-3">${ln}</p>`); }
  }
  if (inList) out.push("</ul>");
  return out.join("\n");
}

export default function ArticlesAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [view, setView] = useState("list"); // "list" | "calendar"

  // AI panel state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiTone, setAiTone] = useState("warm");
  const [aiLength, setAiLength] = useState("mittel");
  const [aiBusy, setAiBusy] = useState(false);

  // Image-gen state
  const [imgBusy, setImgBusy] = useState(false);
  const [inlineOpen, setInlineOpen] = useState(false);
  const [inlinePrompt, setInlinePrompt] = useState("");
  const [inlineStyle, setInlineStyle] = useState("photo");
  const [inlineBusy, setInlineBusy] = useState(false);

  const bodyRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/articles").then((r) => setList(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const del = async (id) => {
    if (!window.confirm("Artikel löschen?")) return;
    await api.delete(`/admin/articles/${id}`);
    toast.success("Gelöscht");
    load();
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      slug: edit.slug,
      title: edit.title,
      excerpt: edit.excerpt || "",
      body: edit.body || "",
      cover_image: edit.cover_image || "",
      category: edit.category || "wissen",
      tags: typeof edit.tags === "string"
        ? edit.tags.split(",").map((s) => s.trim()).filter(Boolean)
        : (edit.tags || []),
      published: edit.published !== false,
      author: edit.author || "AdventureCouple.lu",
    };
    try {
      if (edit.id) await api.put(`/admin/articles/${edit.id}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success("Gespeichert");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };

  const newArticle = () => {
    setEdit({
      slug: "", title: "", excerpt: "", body: "## Einleitung\n\n", cover_image: "",
      category: "wissen", tags: "", published: true, author: "AdventureCouple.lu",
    });
    setShowPreview(false);
    setAiOpen(false);
  };

  // --- AI: generate full article from topic ----------------------------------
  const aiGenerate = async () => {
    if (!aiTopic.trim()) {
      toast.error("Bitte ein Thema eingeben");
      return;
    }
    setAiBusy(true);
    try {
      const r = await api.post("/admin/articles/ai-generate", {
        topic: aiTopic, category: edit?.category || "wissen", tone: aiTone, length: aiLength,
      });
      setEdit((prev) => ({
        ...(prev || { published: true, author: "AdventureCouple.lu", cover_image: "" }),
        title: r.data.title,
        slug: r.data.slug,
        excerpt: r.data.excerpt,
        body: r.data.body,
        tags: (r.data.tags || []).join(", "),
        category: r.data.category,
        _cover_prompt: r.data.cover_image_prompt,
      }));
      toast.success("Artikel von KI vorbefüllt – jetzt prüfen & speichern");
      setAiOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "KI-Fehler");
    } finally {
      setAiBusy(false);
    }
  };

  // --- AI: cover image -------------------------------------------------------
  const aiCoverImage = async () => {
    const prompt = edit?._cover_prompt || edit?.title;
    if (!prompt) {
      toast.error("Bitte zuerst einen Titel eingeben");
      return;
    }
    setImgBusy(true);
    try {
      const r = await api.post("/admin/articles/ai-image", { prompt, style: "photo" });
      setEdit((prev) => ({ ...prev, cover_image: r.data.url }));
      toast.success("Titelbild generiert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bild-Fehler");
    } finally {
      setImgBusy(false);
    }
  };

  // --- AI: inline image (inserts ![alt](url) at cursor) ----------------------
  const aiInlineImage = async () => {
    if (!inlinePrompt.trim()) {
      toast.error("Bitte einen Bild-Prompt eingeben");
      return;
    }
    setInlineBusy(true);
    try {
      const r = await api.post("/admin/articles/ai-image", {
        prompt: inlinePrompt, style: inlineStyle,
      });
      const url = r.data.url;
      const alt = inlinePrompt.slice(0, 80);
      const insertion = `\n\n![${alt}](${url})\n\n`;

      // Insert at textarea cursor, or append
      const ta = bodyRef.current;
      const current = edit.body || "";
      let next = current + insertion;
      let nextCursor = next.length;
      if (ta && document.activeElement === ta) {
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        next = current.slice(0, start) + insertion + current.slice(end);
        nextCursor = start + insertion.length;
      }
      setEdit((prev) => ({ ...prev, body: next }));
      requestAnimationFrame(() => {
        if (ta) {
          ta.focus();
          ta.setSelectionRange(nextCursor, nextCursor);
        }
      });
      toast.success("Bild eingefügt");
      setInlineOpen(false);
      setInlinePrompt("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bild-Fehler");
    } finally {
      setInlineBusy(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div data-testid="admin-articles">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="text-sm opacity-70">{list.length} Artikel</div>
          <div className="ml-3 inline-flex rounded-full border p-0.5" style={{ borderColor: "var(--wp-border)" }}>
            <button
              onClick={() => setView("list")}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors"
              style={{
                backgroundColor: view === "list" ? "var(--wp-olive)" : "transparent",
                color: view === "list" ? "var(--wp-cream)" : "var(--wp-forest)",
              }}
              data-testid="articles-view-list"
            >
              <ListIcon className="w-3.5 h-3.5" /> Liste
            </button>
            <button
              onClick={() => setView("calendar")}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors"
              style={{
                backgroundColor: view === "calendar" ? "var(--wp-olive)" : "transparent",
                color: view === "calendar" ? "var(--wp-cream)" : "var(--wp-forest)",
              }}
              data-testid="articles-view-calendar"
            >
              <CalendarIcon className="w-3.5 h-3.5" /> Kalender
            </button>
          </div>
        </div>
        <button onClick={newArticle} className="btn-primary !py-2 !px-4" data-testid="admin-new-article">
          <Plus className="w-4 h-4" /> Neuer Artikel
        </button>
      </div>

      {view === "calendar" ? (
        <ArticlesCalendar />
      ) : (
        <div className="space-y-3">
        {list.length === 0 && <div className="text-sm opacity-70">Noch keine Artikel.</div>}
        {list.map((a) => (
          <div key={a.id} className="plant-card p-4 flex items-start gap-4" data-testid={`admin-article-${a.slug}`}>
            {a.cover_image && <img src={a.cover_image} alt={a.title} className="w-20 h-20 rounded-lg object-cover" />}
            <div className="flex-1">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="font-display text-lg">{a.title}</h4>
                  <div className="text-xs opacity-70 mt-1">/{a.slug} · {a.category} · {a.views || 0} Aufrufe</div>
                  {!a.published && <span className="badge mt-1 inline-block" style={{ background: "rgba(0,0,0,0.06)" }}>Entwurf</span>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => { setEdit({ ...a, tags: (a.tags || []).join(",") }); setShowPreview(false); setAiOpen(false); }} className="text-[var(--wp-primary)]" data-testid={`edit-article-${a.slug}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(a.id)} className="text-[var(--wp-toxic)]" data-testid={`delete-article-${a.slug}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-sm opacity-80 mt-2 line-clamp-2">{a.excerpt}</p>
            </div>
          </div>
        ))}
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(58,57,44,0.65)" }} onClick={() => setEdit(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="article-editor">
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="font-display text-2xl" style={{ color: "var(--wp-olive)" }}>
                {edit.id ? "Artikel bearbeiten" : "Neuer Artikel"}
              </h3>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setAiOpen((v) => !v)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium"
                  style={{ backgroundColor: aiOpen ? "var(--wp-olive)" : "var(--wp-sand)", color: aiOpen ? "var(--wp-cream)" : "var(--wp-forest)" }}
                  data-testid="article-ai-toggle"
                >
                  <Sparkles className="w-4 h-4" /> KI-Assistent
                </button>
              </div>
            </div>

            {/* AI panel */}
            {aiOpen && (
              <div className="mb-5 p-4 rounded-xl border" style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-cream)" }} data-testid="article-ai-panel">
                <div className="overline mb-2">KI-Artikelgenerator</div>
                <input
                  placeholder="Thema, z.B. «Bärlauch sicher erkennen und sammeln"
                  value={aiTopic}
                  onChange={(e) => setAiTopic(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border text-sm mb-3"
                  style={{ borderColor: "var(--wp-border)" }}
                  data-testid="article-ai-topic"
                />
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <select value={aiTone} onChange={(e) => setAiTone(e.target.value)} className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }}>
                    {TONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <select value={aiLength} onChange={(e) => setAiLength(e.target.value)} className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }}>
                    {LENGTHS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <select value={edit.category || "wissen"} onChange={(e) => setEdit({ ...edit, category: e.target.value })} className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }}>
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={aiGenerate}
                  disabled={aiBusy || !aiTopic.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                  style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)", opacity: (aiBusy || !aiTopic.trim()) ? 0.6 : 1 }}
                  data-testid="article-ai-generate"
                >
                  <Sparkles className="w-4 h-4" />
                  {aiBusy ? "KI schreibt …" : "Artikel mit KI vorbefüllen"}
                </button>
                <p className="text-xs opacity-60 mt-2">Claude Sonnet 4.5 erstellt Titel, Slug, Excerpt, Markdown-Body und Tags. Du kannst alles nachbearbeiten.</p>
              </div>
            )}

            <form onSubmit={save} className="grid grid-cols-2 gap-3 text-sm">
              <input required placeholder="Titel" value={edit.title || ""} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="col-span-2 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="article-form-title" />
              <input required placeholder="Slug (URL-Pfad, z.B. brennnessel-info)" value={edit.slug || ""} onChange={(e) => setEdit({ ...edit, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="article-form-slug" />
              <select value={edit.category || "wissen"} onChange={(e) => setEdit({ ...edit, category: e.target.value })} className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <textarea placeholder="Kurzbeschreibung (Excerpt)" value={edit.excerpt || ""} onChange={(e) => setEdit({ ...edit, excerpt: e.target.value })} className="col-span-2 px-3 py-2 rounded-lg border min-h-[60px]" style={{ borderColor: "var(--wp-border)" }} />

              <div className="col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline">Titelbild</div>
                  <button
                    type="button"
                    onClick={aiCoverImage}
                    disabled={imgBusy || !edit.title}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full"
                    style={{ backgroundColor: "var(--wp-sand)", color: "var(--wp-forest)", opacity: (imgBusy || !edit.title) ? 0.6 : 1 }}
                    data-testid="article-ai-cover"
                  >
                    <ImageIcon className="w-3.5 h-3.5" /> {imgBusy ? "Generiere …" : "KI-Titelbild"}
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <ImageUpload value={edit.cover_image} onChange={(v) => setEdit({ ...edit, cover_image: v })} endpoint="/admin/uploads" testId="article-cover-upload" />
                  <input placeholder="oder Bild-URL" value={edit.cover_image || ""} onChange={(e) => setEdit({ ...edit, cover_image: e.target.value })} className="flex-1 px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "var(--wp-border)" }} />
                </div>
              </div>

              {/* Body editor + preview toggle */}
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <div className="overline">Inhalt (Markdown)</div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setInlineOpen((v) => !v)}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: "var(--wp-sand)", color: "var(--wp-forest)" }}
                      data-testid="article-inline-image-toggle"
                    >
                      <ImageIcon className="w-3.5 h-3.5" /> Bild einfügen
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowPreview((v) => !v)}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border"
                      style={{ borderColor: "var(--wp-border)" }}
                      data-testid="article-preview-toggle"
                    >
                      {showPreview ? <><EyeOff className="w-3.5 h-3.5" /> Editor</> : <><Eye className="w-3.5 h-3.5" /> Vorschau</>}
                    </button>
                  </div>
                </div>

                {inlineOpen && (
                  <div className="mb-3 p-3 rounded-lg border" style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-cream)" }}>
                    <input
                      placeholder="Beschreibung des Bildes, z.B. «junger Bärlauch im Frühlingswald, Morgensonne"
                      value={inlinePrompt}
                      onChange={(e) => setInlinePrompt(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border text-sm mb-2"
                      style={{ borderColor: "var(--wp-border)" }}
                      data-testid="article-inline-image-prompt"
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <select value={inlineStyle} onChange={(e) => setInlineStyle(e.target.value)} className="px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "var(--wp-border)" }}>
                        <option value="photo">Fotorealistisch</option>
                        <option value="illustration">Botanische Illustration</option>
                      </select>
                      <button
                        type="button"
                        onClick={aiInlineImage}
                        disabled={inlineBusy || !inlinePrompt.trim()}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full"
                        style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)", opacity: (inlineBusy || !inlinePrompt.trim()) ? 0.6 : 1 }}
                        data-testid="article-inline-image-generate"
                      >
                        <Sparkles className="w-3.5 h-3.5" /> {inlineBusy ? "Generiere …" : "Generieren & einfügen"}
                      </button>
                    </div>
                  </div>
                )}

                {showPreview ? (
                  <div
                    className="px-4 py-3 rounded-lg border min-h-[260px] prose-sm"
                    style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-cream)" }}
                    data-testid="article-preview"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(edit.body || "") }}
                  />
                ) : (
                  <textarea
                    ref={bodyRef}
                    required
                    placeholder="## Überschrift&#10;&#10;Dein Text in **Markdown**. Verwende ![alt](url) für Bilder."
                    value={edit.body || ""}
                    onChange={(e) => setEdit({ ...edit, body: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border font-mono text-xs min-h-[320px]"
                    style={{ borderColor: "var(--wp-border)" }}
                    data-testid="article-form-body"
                  />
                )}
              </div>

              <input placeholder="Tags (kommagetrennt)" value={edit.tags || ""} onChange={(e) => setEdit({ ...edit, tags: e.target.value })} className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
              <input placeholder="Autor" value={edit.author || ""} onChange={(e) => setEdit({ ...edit, author: e.target.value })} className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
              <label className="col-span-2 flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={edit.published !== false} onChange={(e) => setEdit({ ...edit, published: e.target.checked })} data-testid="article-form-published" />
                Veröffentlicht (sonst Entwurf)
              </label>
              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <button type="button" onClick={() => setEdit(null)} className="btn-secondary">Abbrechen</button>
                <button className="btn-primary" data-testid="article-form-submit">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
